// System 5 — locomotion foundation, then the robotic fauna on top of it.
//
// The brief is explicit about the order: "build the shared rig, IK and gait system here first, then
// the fauna on top of it. Systems 6 and 7 will reuse it." So this file is three layers, and the top
// one is deliberately thin:
//
//   1. RIG        — an Object3D bone tree with rigid parts, plus a two-bone analytic IK solver.
//   2. GAIT       — phase-driven footfall, stance/swing, body carriage derived from the feet.
//   3. CREATURES  — data. A creature is a spec object; nothing below layer 3 knows what a fauna is.
//
// RIGID PARTS, NOT SKINNING. Each bone carries its own mesh. A skinned mesh would need weights, and
// weights fail silently -- a badly-weighted joint still renders, just wrongly. A rigid part bolted
// to a bone either is in the right place or is visibly detached, which is a defect you can see.
// This is a machine, so hard part boundaries are also correct rather than a compromise.

import * as THREE from 'three';
import { rngFor } from './rng.js';
import { quadBody, quadHead, quadLimb, dressBone, cloneParts, cached } from './geometry/rigs.js';
import { strut } from './geometry/procedural.js';
import { footRoll, gaitDip } from './gaitcurves.js';

// ---------------------------------------------------------------------------
// 1. RIG
// ---------------------------------------------------------------------------

/**
 * Analytic two-bone IK in a plane.
 *
 * Returns the two joint angles that place the chain end at `target` distance away, given segment
 * lengths a and b. Angles are relative to the direction of the target.
 *
 * Clamped to just inside full extension. A chain solved exactly straight has a singular knee -- the
 * pole vector stops constraining it and the joint pops to an arbitrary side on the next frame,
 * which reads as a snap. 0.999 costs nothing and removes the whole failure mode.
 */
export function solveTwoBone(a, b, dist) {
  const d = Math.min(Math.max(dist, Math.abs(a - b) + 1e-4), (a + b) * 0.999);
  // Law of cosines.
  const cosRoot = (a * a + d * d - b * b) / (2 * a * d);
  const cosKnee = (a * a + b * b - d * d) / (2 * a * b);
  return {
    root: Math.acos(Math.min(1, Math.max(-1, cosRoot))),
    knee: Math.PI - Math.acos(Math.min(1, Math.max(-1, cosKnee))),
    reached: dist <= (a + b) * 0.999,
  };
}

/** A leg: hip -> thigh -> shin -> foot, driven by a world-space foot target. */
export class Leg {
  constructor(root, spec) {
    this.spec = spec;
    this.hip = new THREE.Group();
    this.hip.position.set(...spec.hip);
    root.add(this.hip);

    this.thigh = new THREE.Group();
    this.hip.add(this.thigh);
    this.shin = new THREE.Group();
    this.shin.position.set(0, -spec.upper, 0);
    this.thigh.add(this.shin);
    this.foot = new THREE.Group();
    this.foot.position.set(0, -spec.lower, 0);
    this.shin.add(this.foot);

    // World-space targets, updated by the gait.
    this.target = new THREE.Vector3(spec.hip[0], 0, spec.hip[2]);
    this.planted = new THREE.Vector3().copy(this.target);
    this.phase = spec.phase;
    this.contact = true;
    this._v = new THREE.Vector3();
    this._m = new THREE.Matrix4();
    this._qa = new THREE.Quaternion();
    this._qb = new THREE.Quaternion();
    this._ea = new THREE.Euler();
  }

  /** Solve the chain so the foot sits on `target`. Called after the body transform is final. */
  solve(bodyMatrixInverse) {
    // Bring the world target into hip space.
    this._v.copy(this.target).applyMatrix4(bodyMatrixInverse);
    const local = this._v.sub(this.hip.position);
    const dist = local.length();
    const { root, knee } = solveTwoBone(this.spec.upper, this.spec.lower, dist);

    // Point the thigh at the target, then rotate back off it by the root angle.
    //
    // The knee-direction sign k flips ALL THREE rotations or none. The S5 round-4 patch flipped
    // only the shin, which made every k=-1 (front) leg miss its target by ~2*root -- about a metre
    // on the Sentinel, the horizontal "collapsing tripod" limb. The pose fence never saw it
    // because it published L.target, the solver's INPUT, as if it were the pose: the tautology of
    // comparing a solved position to the target that placed it, one step worse. The ikError fence
    // in contact.mjs now measures the ACHIEVED chain end against the target independently.
    const k = this.spec.knee || 1;
    // HINGE CONSTRAINTS (polish brief): the analytic solver picks the bend side by k, but a target
    // that wanders behind or across the hip can flip the yaw 180 degrees or drive the thigh past
    // mechanical limits. Clamp yaw to a hip's real lateral play and the total lean to the joint
    // stop -- a clamped foot lands short by centimetres; a flipped knee is a broken machine.
    const clampJ = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
    // AZIMUTH IS NOT A HINGE. A trailing foot sits at azimuth ~pi in this yaw-then-pitch
    // decomposition -- that flip is how the solver says "behind the hip", not a broken joint.
    // The polish round clamped it to +/-0.75 as "lateral play" and silently bent every trailing
    // leg sideways; the ikError fence read 0.14-0.38 m the next time contact.mjs ran. Inversion
    // protection lives where it belongs: the knee-side sign k and the per-spec flexion ranges.
    const yaw = Math.atan2(local.x, local.z);
    // pitchHi defaults to the grounded gait's 1.45; a leg that legitimately reaches ABOVE its
    // hip (a dog offering its paw) declares a higher ceiling in its spec. The first shake fence
    // run caught this: the paw stopped 0.44 m short, pinned to the old below-hip assumption.
    const pitchToTarget = clampJ(Math.atan2(Math.hypot(local.x, local.z), -local.y), 0, this.spec.pitchHi ?? 1.45);
    // Per-spec hinge ranges (master brief): the defaults reproduce the old global clamps, so only
    // specs that declare ranges change behaviour. kn is the CLAMPED flexion actually applied.
    // Default lean range is PERMISSIVE (+/-1.5): "lean" here is the thigh's total world angle,
    // pitch + k*root -- and root is knee-bend allocation, not posture. A -0.55 default (tuned on
    // k=+1 legs) silently clamped every k=-1 z-fold leg, whose stance lean is ~-0.75, and the ik
    // fence read 0.26-0.34 m on the Sentinel. Specs narrow this deliberately; defaults must not.
    const lo = this.spec.leanLo ?? -1.5, hi = this.spec.leanHi ?? 1.5;
    const kn = clampJ(knee, this.spec.kneeLo ?? 0, this.spec.kneeHi ?? 2.6);
    // rotateX(theta) on a Y-down bone gives world lean alpha = MINUS theta, so aiming at a target
    // leaning +A forward needs rotateX(-A). The original solver used rotateX(A - root): fore-aft
    // MIRRORED from birth -- achieved feet landed at -h instead of +h, zero error straight down,
    // 2h everywhere else. Symmetric gaits kept it looking plausible for a whole system; the
    // achieved-vs-target fence caught it on its first run, measuring 0.7-1.1 m of miss.
    // Derivation checked numerically: a=b=1, d=1.2, target straight down closes to 0.000.
    this.thigh.rotation.set(0, 0, 0);
    this.thigh.rotateY(yaw);
    this.thigh.rotateX(-clampJ(pitchToTarget + k * root, lo, hi));
    this.shin.rotation.set(k * kn, 0, 0);
    // FOOT ORIENTATION IS NOT THE CHAIN'S ORIENTATION.
    //
    // The thigh's yaw is the AZIMUTH OF THE TARGET -- necessary to aim the two-bone chain, but
    // meaningless as a foot direction, and unstable: a planted stance foot passes directly under
    // the hip every step, where local.x and local.z both approach zero and atan2 swings through
    // +/-90 degrees. The foot inherited that swing, so the feet turned in toward each other and
    // the hips appeared to wrench round to follow. (Clamping the yaw was tried and is wrong for a
    // different reason -- it breaks trailing legs; the azimuth is legitimate for REACHING.)
    //
    // So: aim the chain with the azimuth, then cancel it at the ankle. The parent chain
    // accumulates Ry(yaw) * Rx(a + b); the foot's local rotation is that inverted, times the
    // orientation actually wanted -- flat along the body's forward axis, plus the heel-to-toe
    // roll. Ankle POSITION is untouched, so the ikError fence is unaffected.
    const roll = this._roll ? -this._roll * (Math.PI / 180) : 0;
    const chainX = -clampJ(pitchToTarget + k * root, lo, hi) + k * kn;
    this._qa.setFromEuler(this._ea.set(0, yaw, 0));
    this._qb.setFromEuler(this._ea.set(chainX, 0, 0));
    this._qa.multiply(this._qb).invert();
    this._qb.setFromEuler(this._ea.set(roll, 0, 0));
    this.foot.quaternion.copy(this._qa.multiply(this._qb));
  }
}

// ---------------------------------------------------------------------------
// 2. GAIT
// ---------------------------------------------------------------------------

/**
 * Footfall driver.
 *
 * Each leg owns a phase in [0,1). Below `duty` it is in STANCE: the foot stays where it was planted
 * and the body travels over it. Above `duty` it is in SWING: the foot arcs to where the body will
 * be when it lands.
 *
 * The stance foot being world-locked is the whole thing. A leg that merely swings back and forth
 * under a moving body is what makes a walk cycle read as a toy -- the foot slides, and a viewer
 * registers that instantly without being able to name it.
 */
export class Gait {
  constructor(spec) {
    // EXAGGERATED (goose-step) swing. Note what is NOT exaggerated: the STRIDE. Distance per
    // cycle is locked to the swing displacement (rate = 1/stride) and the swing reach is capped
    // at 0.45 * leg length, so stretching the stride past ~0.8 m would make the body travel
    // further than the foot can be repositioned -- and the deficit comes out as a sliding stance
    // foot, which is the S5 defect this rig was rebuilt to eliminate. What IS exaggerated is the
    // swing PATH: the leg straightens and rises to hip height mid-swing instead of clearing the
    // ground by 5 cm. That stresses the solver (near-full extension at the top of the arc) while
    // leaving the no-slip invariant intact.
    this.exaggerated = !!spec.exaggerated;
    this.duty = spec.duty;            // fraction of the cycle spent in stance
    this.stride = spec.stride;
    this.lift = spec.lift;
    // RATE IS DERIVED, NOT AUTHORED.
    //
    // rate is cycles per metre travelled, so distance-per-cycle is 1/rate. If that does not equal
    // the STRIDE -- how far the swing actually repositions the foot -- then a world-locked stance
    // foot is being asked to hold while the body travels further than the foot will ever move, and
    // the deficit comes out as the leg over-extending into a straight strut and then snapping.
    //
    // A critic saw exactly that: "over-reaches into a near-straight strut and then freezes in that
    // pose while the body keeps translating." Authored rate was 0.72-0.92 (1.09-1.39 m per cycle)
    // against a 0.42-0.55 m stride -- a deficit of ~0.7 m per cycle. Locking them together removes
    // the whole class of error, and makes it impossible to reintroduce by tuning one number.
    this.rate = 1 / spec.stride;
    this.t = 0;
  }

  advance(distance) { this.t = (this.t + distance * this.rate) % 1; }

  /**
   * Where a leg's foot should be, given the body's current position/heading and its phase.
   * `groundAt` samples the world so the creature walks on the actual pavement, kerb and stair.
   */
  footTarget(leg, bodyPos, heading, speed, groundAt, out) {
    const ph = (this.t + leg.phase) % 1;
    // Heel-to-toe roll, per leg, from the SAME phase that drives the footfall -- so the heel
    // cannot strike at a different moment than the foot plants.
    leg._roll = leg.spec.roll ? footRoll(ph, this.duty) : 0;
    const s = Math.sin(heading), c = Math.cos(heading);
    const home = leg.spec.hip;
    // Home position of this foot in world space, ignoring stride.
    const hx = bodyPos.x + home[0] * c + home[2] * s;
    const hz = bodyPos.z - home[0] * s + home[2] * c;

    // Reach is EXACTLY half a stride. An earlier version scaled it by speed/0.9, which quietly
    // re-broke the S5 invariant (swing displacement must equal body travel per cycle) whenever
    // speed differed from 0.9 -- the deficit accumulated and legs trailed further every cycle.
    const reach = Math.min(this.stride * 0.5, (leg.spec.upper + leg.spec.lower) * 0.45);
    const landX = hx + Math.sin(heading) * reach;
    const landZ = hz + Math.cos(heading) * reach;

    if (ph < this.duty) {
      // STANCE. Plant ON THE TRANSITION, not in a sampled window. The previous code re-planted
      // only when a frame landed inside the last 1.5% of swing -- an 8 ms window at 16.7 ms steps
      // for the Sentinel -- so legs that missed it kept a plant point from a full cycle ago and the
      // IK stretched them into the horizontal flail a critic called "a collapsing camera tripod".
      // A phase CROSSING is an event every step can observe; a phase WINDOW is a lottery.
      if (leg._wasSwing) {
        leg.planted.set(landX, groundAt(landX, landZ), landZ);
        leg._wasSwing = false;
      }
      leg.contact = true;
      out.copy(leg.planted);
    } else {
      leg._wasSwing = true;
      leg.contact = false;
      const u = (ph - this.duty) / (1 - this.duty);
      const ax = hx - Math.sin(heading) * reach;
      const az = hz - Math.cos(heading) * reach;
      if (this.exaggerated) {
        // Quadratic Bezier through a mid-swing APEX: leg straight out front at hip height. The
        // control point is solved so the curve passes exactly through the apex at u = 0.5
        // (B(0.5) = P0/4 + P1/2 + P2/4, hence P1 = 2*apex - (P0 + P2)/2), because "near the apex"
        // and "at the apex" are different tests of the solver's extension limit.
        const legLen = (leg.spec.upper + leg.spec.lower);
        const apexX = hx + Math.sin(heading) * legLen * 0.92;
        const apexZ = hz + Math.cos(heading) * legLen * 0.92;
        const apexY = bodyPos.y + leg.spec.hip[1];
        const gA = groundAt(ax, az), gB = groundAt(landX, landZ);
        const cx = 2 * apexX - (ax + landX) / 2;
        const cz = 2 * apexZ - (az + landZ) / 2;
        const cy = 2 * apexY - (gA + gB) / 2;
        const w0 = (1 - u) * (1 - u), w1 = 2 * (1 - u) * u, w2 = u * u;
        out.x = w0 * ax + w1 * cx + w2 * landX;
        out.z = w0 * az + w1 * cz + w2 * landZ;
        out.y = w0 * gA + w1 * cy + w2 * gB;
      } else {
        out.x = ax + (landX - ax) * u;
        out.z = az + (landZ - az) * u;
        const g = groundAt(out.x, out.z);
        // Lift arc: sine, biased so the foot clears early and settles late. A symmetric arc reads
        // as a bounce; real swing clears fast then feels for the ground.
        out.y = g + Math.sin(Math.pow(u, 0.72) * Math.PI) * this.lift;
      }
    }
    return out;
  }
}

// ---------------------------------------------------------------------------
// Part building -- code-built boxes, one per bone
// ---------------------------------------------------------------------------
export function part(parent, mat, w, h, d, x, y, z, rx = 0, ry = 0, rz = 0) {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  m.position.set(x, y, z);
  m.rotation.set(rx, ry, rz);
  m.castShadow = true;
  m.receiveShadow = true;
  parent.add(m);
  return m;
}

export function tube(parent, mat, r, len, x, y, z, rx = 0, rz = 0) {
  const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r * 0.92, len, 8), mat);
  m.position.set(x, y, z);
  m.rotation.set(rx, 0, rz);
  m.castShadow = true;
  parent.add(m);
  return m;
}

// ---------------------------------------------------------------------------
// 3. CREATURES
// ---------------------------------------------------------------------------

/**
 * Robotic fauna. The brief: "Creature-shaped machines that patrol fixed routes and never attack.
 * They react to the player -- head turns, a pause, stepping aside -- and then continue what they
 * were doing. Treat them as wildlife, not as enemies that happen to be passive. They should look
 * like they have somewhere to be."
 *
 * "Somewhere to be" is the design constraint that matters. The reaction is an INTERRUPTION of a
 * task, not a state the creature lives in: it notices, deals with you, and resumes the same errand
 * at the same speed. A creature whose default state is "reacting to the player" is an enemy with
 * the damage turned off.
 */
export class Fauna {
  constructor(scene, world, spec, mats, key) {
    this.spec = spec;
    this.world = world;
    this.key = key;
    const r = rngFor(`fauna.${key}`);

    this.root = new THREE.Group();
    this.body = new THREE.Group();
    this.root.add(this.body);
    scene.add(this.root);

    this.legs = spec.legs.map((L) => new Leg(this.body, L));
    this.gait = new Gait(spec.gait);

    // --- parts (overhaul 3B) -------------------------------------------------
    // Chassis archetype from the pool: exposed spine, side rails, part-open cowling, dual-shaft
    // limbs with knee pistons split across thigh/shin so the RIG drives the extension. One welded
    // vertex-colored mesh per bone; bones, pivots and behaviour untouched. The glow eye keeps its
    // own material (behaviour brightens it on notice).
    const glow = mats.glow;
    const B = spec.size;
    const chassis = spec.chassis ?? 0;
    dressBone(this.body, cloneParts(cached(`quad.body.${chassis}`, () =>
      quadBody(chassis, 1, { w: B.w, h: B.h, l: B.l, hips: spec.legs.map((L) => L.hip) }))), 'mech');
    this.neck = new THREE.Group();
    this.neck.position.set(0, B.h * 0.32, B.l * 0.46);
    this.body.add(this.neck);
    // 3B critic: "the head floats -- visible neck gap". A single thin strut did not read as a
    // load path; the neck is now a thick main member plus a diagonal brace to the head base.
    dressBone(this.neck, cloneParts(cached('quad.neck', () => [
      ...strut([0, -0.03, -0.02], [0, 0.21, 0.10], 0.045, 0.034, { slot: 'mechDark', seg: 8 }),
      ...strut([0, -0.01, 0.05], [0, 0.19, 0.115], 0.016, 0.014, { slot: 'trim', seg: 5 }),
    ])), 'mech');
    this.head = new THREE.Group();
    this.head.position.set(0, 0.21, 0.10);
    this.neck.add(this.head);
    dressBone(this.head, cloneParts(cached(`quad.head.${chassis}`, () => quadHead(chassis, 1))), 'mech');
    this.eye = part(this.head, glow, 0.10, 0.026, 0.012, 0, 0.008, 0.140);
    // FP pet-touch anchor: top of the head cowling. The FP palm snaps to THIS measured point.
    this.petPoint = new THREE.Object3D();
    this.petPoint.position.set(0, 0.09, 0.08);
    this.head.add(this.petPoint);

    const limb = cached(`quad.limb.${chassis}.${spec.legs[0].upper.toFixed(3)}`, () =>
      quadLimb(spec.legs[0].upper, spec.legs[0].lower, 1));
    for (const L of this.legs) {
      dressBone(L.thigh, cloneParts(limb.thigh), 'mech');
      dressBone(L.shin, cloneParts(limb.shin), 'mech');
      dressBone(L.foot, cloneParts(limb.foot), 'mech');
    }

    // --- route -------------------------------------------------------------
    this.route = spec.route;
    this.seg = 0;
    this.u = r.f();
    this.pos = new THREE.Vector3();
    this.heading = 0;
    this.speed = spec.speed;
    this.baseSpeed = spec.speed;

    // Behaviour state. Default is ERRAND; everything else is a temporary interruption.
    this.state = 'errand';
    this.stateT = 0;
    this.headYaw = 0;
    this.headYawTarget = 0;
    this.sideStep = 0;
    this.sideStepTarget = 0;
    this.noticed = false;
    // The RIGHT front leg, DERIVED from the hip positions rather than hard-coded: the creature
    // faces +Z with up +Y, so its right is -X (z cross y = -x) and its front is +Z. The first
    // handshake build reached with legs[1] -- the LEFT paw -- because an index looked like a
    // side. Indices are not sides; the geometry is.
    this.shakeLeg = spec.legs.findIndex((L) => L.hip[0] < 0 && L.hip[2] > 0);
    this.interact = null;
    this._ihead = null;
    this.clearX = 0; this.clearZ = 0;   // world-obstacle clearance, decays back to the route
    this._tmp = new THREE.Vector3();
    this._inv = new THREE.Matrix4();

    // Plant every foot before the first frame, or the first solve reaches for (0,0,0).
    this._placeOnRoute(0);
    for (const L of this.legs) {
      const s = Math.sin(this.heading), c = Math.cos(this.heading);
      const hx = this.pos.x + L.spec.hip[0] * c + L.spec.hip[2] * s;
      const hz = this.pos.z - L.spec.hip[0] * s + L.spec.hip[2] * c;
      L.planted.set(hx, world.groundAt(hx, hz, 0), hz);
      L.target.copy(L.planted);
    }
  }

  _placeOnRoute(du) {
    const R = this.route;
    this.u += du;
    while (this.u >= 1) { this.u -= 1; this.seg = (this.seg + 1) % R.length; }
    const a = R[this.seg], b = R[(this.seg + 1) % R.length];
    this.pos.x = a[0] + (b[0] - a[0]) * this.u;
    this.pos.z = a[1] + (b[1] - a[1]) * this.u;
    this.heading = Math.atan2(b[0] - a[0], b[1] - a[1]);
  }

  /**
   * @returns {object} event counters for the interaction gate. The behaviour must be OBSERVABLE
   * from outside, or the gate cannot tell a working reaction from a stub.
   */
  update(dt, playerPos) {
    const ev = {};
    const dx = playerPos.x - this.pos.x, dz = playerPos.z - this.pos.z;
    const dist = Math.hypot(dx, dz);

    // E-INTERACTION (FP brief): while being petted / offered a hand, the dog is pinned in a
    // friendly halt -- attentive, stopped, and consequence-free by construction.
    if (this.interact) { this.interact.t += dt; this.state = 'halt'; this.stateT = 0; }

    // --- reaction ----------------------------------------------------------
    const NOTICE = 6.5, YIELD = 2.6, HALT = 1.85;
    if (dist < NOTICE) {
      // Look at the player. This is the head turn.
      const bearing = Math.atan2(dx, dz) - this.heading;
      this.headYawTarget = Math.atan2(Math.sin(bearing), Math.cos(bearing));
      this.headYawTarget = Math.max(-1.25, Math.min(1.25, this.headYawTarget));
      if (!this.noticed) { this.noticed = true; ev['fauna.notice'] = 1; }
      if (dist < HALT && this.state !== 'halt') {
        // FRIENDLY HALT (polish brief): inside 1.85 m the dog smoothly stops and watches -- a
        // curious street animal, never a collider and never a threat.
        this.state = 'halt';
        this.stateT = 0;
        ev['fauna.halt'] = 1;
      } else if (dist < YIELD && this.state === 'errand') {
        this.state = 'yield';
        this.stateT = 0;
        ev['fauna.yield'] = 1;
      }
    } else {
      this.headYawTarget = 0;
      if (this.noticed) { this.noticed = false; }
    }

    // --- state -------------------------------------------------------------
    this.stateT += dt;
    if (this.state === 'halt') {
      this.speed += (0 - this.speed) * Math.min(1, dt * 5.0);
      if (dist > 2.6) { this.state = 'resume'; this.stateT = 0; ev['fauna.resume'] = 1; }
    } else if (this.state === 'yield') {
      // Pause, step aside, then RESUME THE ERRAND. It does not follow, threaten, or wait for you.
      const s = Math.min(1, this.stateT / 0.55);
      this.speed = this.baseSpeed * (1 - 0.85 * s);
      this.sideStepTarget = (dx * Math.cos(this.heading) - dz * Math.sin(this.heading)) > 0 ? -0.42 : 0.42;
      if (this.stateT > 1.9 || dist > YIELD * 1.6) {
        this.state = 'resume';
        this.stateT = 0;
        ev['fauna.resume'] = 1;
      }
    } else if (this.state === 'resume') {
      const s = Math.min(1, this.stateT / 1.1);
      this.speed = this.baseSpeed * (0.15 + 0.85 * s);
      this.sideStepTarget *= (1 - s);
      if (s >= 1) { this.state = 'errand'; this.sideStepTarget = 0; }
    } else {
      this.speed = this.baseSpeed;
      this.sideStepTarget = 0;
    }

    // Smooth the head and the sidestep. Snapping either reads as a state machine, not an animal.
    this.headYaw += (this.headYawTarget - this.headYaw) * Math.min(1, dt * 5.5);
    this.sideStep += (this.sideStepTarget - this.sideStep) * Math.min(1, dt * 3.2);
    this.neck.rotation.y = this.headYaw * 0.42;
    this.head.rotation.y = this.headYaw * 0.58;
    this.head.rotation.x = Math.max(-0.35, Math.min(0.35, -(playerPos.y - (this.pos.y + 0.7)) * 0.22));

    // --- travel ------------------------------------------------------------
    const R = this.route;
    const a = R[this.seg], b = R[(this.seg + 1) % R.length];
    const segLen = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const travel = this.speed * dt;
    this._placeOnRoute(travel / Math.max(0.001, segLen));
    if (this.interact) {
      // Square to the player: the route re-asserts its heading every frame, so the interaction
      // keeps its own smoothed bearing and overrides it for the duration of the touch.
      if (this._ihead === null) this._ihead = this.heading;
      let iw = Math.atan2(dx, dz) - this._ihead;
      iw = Math.atan2(Math.sin(iw), Math.cos(iw));
      this._ihead += iw * Math.min(1, dt * 3.5);
      this.heading = this._ihead;
    } else this._ihead = null;
    this.gait.advance(travel);

    // Lateral offset for the sidestep, in body space.
    this.pos.x += Math.cos(this.heading) * this.sideStep;
    this.pos.z -= Math.sin(this.heading) * this.sideStep;

    // WORLD COLLISION (polish brief): the route gives a desired point; a persistent clearance
    // offset (decaying toward the route) is resolved against the solids -- bins, bollards, parked
    // cars -- so the dog STEERS AROUND obstacles and rejoins its path rather than clipping.
    {
      const decay = Math.max(0, 1 - dt * 0.9);
      this.clearX *= decay; this.clearZ *= decay;
      const bx = this.pos.x, bz = this.pos.z;
      const [rx, rz] = this.world.resolve(bx + this.clearX, bz + this.clearZ, 0.42,
        this.pos.y + 0.05, this.pos.y + 0.85);
      this.clearX = rx - bx; this.clearZ = rz - bz;
      this.pos.x = rx; this.pos.z = rz;
    }

    // --- body carriage, derived FROM the feet -------------------------------
    // Height, pitch and roll all come out of where the feet actually are, so the body cannot float
    // over a kerb the legs are standing on. This is why the ground sampler is passed down.
    const ga = (x, z) => this.world.groundAt(x, z, 0);
    for (const L of this.legs) {
      this.gait.footTarget(L, this.pos, this.heading, this.speed, ga, L.target);
    }
    // HANDSHAKE (FP brief): the front-right paw leaves the gait and reaches the SAME meet
    // point the player's FP palm snaps to. Two solvers, one world point -- the touch is
    // physical by construction, and the gate measures both distances anyway.
    if (this.interact && this.interact.mode === 'shake' && this.interact.meet) {
      const L = this.legs[this.shakeLeg];
      const u = Math.min(1, Math.max(0, (this.interact.t - 0.12) / 0.5));
      const s2 = u * u * (3 - 2 * u);
      L.target.lerp(this.interact.meet, s2);
      L.contact = s2 < 0.5;
    }
    // Left as the all-feet average deliberately. Switching the quadruped to contact-only feet was
    // tried and moved its ik error 0.018 -> 0.047 m: at duty 0.52 a diagonal pair is always down,
    // so the average is already close to the planted feet, and the change buys nothing while
    // shifting a fenced baseline. The biped case (see cyborgs.js) is where it matters.
    let sumY = 0, n = 0, fore = 0, aft = 0, left = 0, right = 0, lowest = Infinity;
    for (const L of this.legs) {
      lowest = Math.min(lowest, L.target.y);
      { sumY += L.target.y; n++; }
      if (L.spec.hip[2] > 0) fore += L.target.y; else aft += L.target.y;
      if (L.spec.hip[0] > 0) right += L.target.y; else left += L.target.y;
    }
    const standY = (n ? sumY / n : lowest) + this.spec.size.ride;
    this.pos.y += (standY - this.pos.y) * Math.min(1, dt * 7.0);

    this.root.position.copy(this.pos);
    this.root.rotation.y = this.heading;
    const half = n / 2;
    this.body.rotation.x = Math.max(-0.22, Math.min(0.22, (aft - fore) / half * 0.9))
      + Math.sin(this.gait.t * Math.PI * 4) * 0.0436;   // rhythmic spine pitch: 2.5 deg at 2x cycle
    this.body.rotation.z = Math.max(-0.16, Math.min(0.16, (right - left) / half * 0.8));
    // Body bob at twice stride frequency, which is what a walking gait produces.
    // Pelvis dips on foot impact (polish brief): weight arrives, the suspension takes it.
    this.body.position.y = -gaitDip(this.gait.t) * 0.025;
    if (this.interact) {
      const I = this.interact, k = Math.min(1, I.t / 0.5);
      if (I.mode === 'pet') {
        // Lean the head up into the touch and wag the whole chassis -- ears and tail are welded
        // into their parent meshes (rigid-part law), so the response lives in head, body and eye.
        this.head.rotation.x -= 0.24 * k;
        this.body.rotation.z += Math.sin(I.t * 7) * 0.035 * k;
      } else if (I.mode === 'shake') {
        this.body.rotation.x -= 0.055 * k;    // weight back off the offered paw
      }
    }

    // --- solve legs ---------------------------------------------------------
    this.root.updateMatrixWorld(true);
    this._inv.copy(this.body.matrixWorld).invert();
    for (const L of this.legs) L.solve(this._inv);

    // Eye brightens when it has noticed you. Diegetic: the world tells you, no UI.
    if (this.eye.material.emissiveIntensity !== undefined) {
      const want = this.noticed ? 2.6 : 1.0;
      this.eye.material.emissiveIntensity += (want - this.eye.material.emissiveIntensity) * Math.min(1, dt * 4);
    }
    return ev;
  }

  /** Feet currently in stance. Used by the contact-sheet gate to prove the gait is real. */
  contactCount() { return this.legs.reduce((n, L) => n + (L.contact ? 1 : 0), 0); }
}

// ---------------------------------------------------------------------------
// Specs
// ---------------------------------------------------------------------------
export function faunaMaterials() {
  return {
    shell: new THREE.MeshStandardMaterial({ color: 0x3c4148, roughness: 0.48, metalness: 0.72 }),
    dark: new THREE.MeshStandardMaterial({ color: 0x15171c, roughness: 0.62, metalness: 0.55 }),
    glow: new THREE.MeshStandardMaterial({
      color: 0x0a0c10, emissive: 0x66ffcc, emissiveIntensity: 1.0, roughness: 0.3,
    }),
  };
}

/** A four-legged street strider: dog-sized, trotting, unbothered. */
export function striderSpec(route, seed = 0) {
  const r = rngFor(`strider.${seed}`);
  const UP = r.range(0.27, 0.32), LO = r.range(0.29, 0.34);
  const HW = 0.19, HL = 0.30;   // hip outboard of the 0.30-wide torso, so legs emerge from its side
  const spec = {
    chassis: seed % 4,
    size: { w: 0.30, h: 0.22, l: 0.72, ride: UP * 0.62 + LO * 0.62, bob: 0.012 },
    // Legacy pace draw kept (voided) so the per-dog rng stream, and with it every downstream
    // stride/lift draw, holds still. The real speed is derived AFTER the literal: 0.8 Hz exactly.
    speed: (() => { void r.range(0.85, 1.25); return 0; })(),
    // Trot: diagonal pairs. Phases 0 / 0.5 / 0.5 / 0 put FL+RR together and FR+RL together.
    // STRICT single-sided hinges (master brief): FORE knees bend +Z (k=+1), HIND bend -Z
    // (k=-1), with per-leg flexion ranges so no edge-case target can invert a joint. The
    // brief's 45/50-deg caps are geometrically unreachable at this stance height -- standing
    // flexion is already ~103 deg (cos(knee/2) = ride/legLen = 0.62) -- so the caps here
    // forbid inversion and hyper-fold while keeping the z-fold stance the machine stands on.
    legs: [
      { hip: [-HW, 0.0, HL], upper: UP, lower: LO, phase: 0.00, knee: 1, kneeLo: 0.17, kneeHi: 2.00, pitchHi: 2.55, leanHi: 3.40 },
      { hip: [HW, 0.0, HL], upper: UP, lower: LO, phase: 0.50, knee: 1, kneeLo: 0.17, kneeHi: 2.00, pitchHi: 2.55, leanHi: 3.40 },
      { hip: [-HW, 0.0, -HL], upper: UP, lower: LO, phase: 0.50, knee: -1, kneeLo: 0.17, kneeHi: 2.10 },
      { hip: [HW, 0.0, -HL], upper: UP, lower: LO, phase: 0.00, knee: -1, kneeLo: 0.17, kneeHi: 2.10 },
    ],
    // duty 0.5 is a true trot: diagonal pairs alternate with no four-foot support phase.
    gait: { duty: 0.52, stride: r.range(0.40, 0.50), lift: r.range(0.055, 0.075) },
    route,
  };
  // MASTER BRIEF: trot cycle at EXACTLY 0.8 Hz. rate = 1/stride cycles per metre, so
  // speed = 0.8 * stride metres/second -- the derivation, not a tuned number.
  spec.speed = 0.8 * spec.gait.stride;
  return spec;
}

/** Patrol routes on the walkable street, kept clear of the parked vehicles. */
export function faunaRoutes() {
  return [
    [[-6.6, -30], [-6.6, 18], [-6.9, 30], [-6.6, 18], [-6.6, -30]],
    [[6.4, 24], [6.4, -16], [6.9, -30], [6.4, -16], [6.4, 24]],
    [[-6.4, 6], [-6.4, -8], [-9.5, -10.2], [-16, -10.2], [-9.5, -10.2], [-6.4, -8], [-6.4, 6]],
  ];
}
